import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Contrat, Justificatif, Vente } from '@troc/shared'
import { eur } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { imprimerContrat, imprimerJustificatif, imprimerTicket } from '../impression/documents'
import { useToast } from '../etat/Toast'

/**
 * Le registre des documents émis.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Ce qu'il répare
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Les contrats, tickets et justificatifs s'imprimaient déjà, mais on ne
 * pouvait les retrouver qu'en sachant de quel client ils venaient. « Montre-moi
 * tout ce qu'on a émis en juin » n'avait pas de réponse — c'est pourtant la
 * question qu'on pose à une comptabilité.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Pourquoi trois totaux et non un seul
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un contrat est une promesse de vente, un ticket un encaissement, un
 * justificatif une sortie de caisse. Les additionner donnerait un nombre qui
 * ne désigne rien. Ils sont donc comptés séparément, et c'est aussi ainsi
 * qu'ils se lisent : ce qui est entré en dépôt, ce qui est rentré en caisse,
 * ce qui est ressorti vers les déposants.
 */

interface Document {
  type: 'contrat' | 'ticket' | 'justificatif'
  cle: string
  numero: string
  date: string
  clientNom: string | null
  clientId: number | null
  montantCentimes: number
  detail: string
}

interface Registre {
  documents: Document[]
  totaux: {
    contrats: { nombre: number; centimes: number }
    tickets: { nombre: number; centimes: number }
    justificatifs: { nombre: number; centimes: number }
  }
}

const TYPES = [
  { cle: 'tous', libelle: 'Tous les documents' },
  { cle: 'contrat', libelle: 'Contrats de dépôt' },
  { cle: 'ticket', libelle: 'Tickets de caisse' },
  { cle: 'justificatif', libelle: 'Justificatifs de vente' },
]

const LIBELLE_TYPE: Record<Document['type'], string> = {
  contrat: 'Contrat',
  ticket: 'Ticket',
  justificatif: 'Justificatif',
}

const FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

function jourIso(decalage = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + decalage)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

export function Documents({ entetes }: { entetes: Record<string, string> }) {
  const { alerter, signaler } = useToast()
  const [registre, setRegistre] = useState<Registre | null>(null)
  const [type, setType] = useState('tous')
  const [du, setDu] = useState(jourIso(-30))
  const [au, setAu] = useState(jourIso())
  const [requete, setRequete] = useState('')
  const [enCours, setEnCours] = useState<string | null>(null)

  const charger = useCallback(() => {
    const p = new URLSearchParams({ du, au, type })
    if (requete) p.set('q', requete)
    api<Registre>(`/documents?${p}`, { entetes })
      .then(setRegistre)
      .catch(() => undefined)
  }, [entetes, du, au, type, requete])

  useEffect(() => {
    const minuterie = window.setTimeout(charger, 220)
    return () => window.clearTimeout(minuterie)
  }, [charger])

  /**
   * Réimpression : on va rechercher le document complet au moment du clic
   * plutôt que de tout charger d'avance. Un registre de six cents lignes qui
   * embarquerait chaque contrat avec ses articles pèserait des mégaoctets pour
   * une réimpression tous les trois jours.
   */
  const reimprimer = async (doc: Document) => {
    setEnCours(doc.type + doc.cle)
    try {
      if (doc.type === 'contrat') {
        const r = await api<{ contrat: Contrat }>(`/contrats/${encodeURIComponent(doc.cle)}`)
        imprimerContrat(r.contrat)
      } else if (doc.type === 'ticket') {
        const r = await api<{ vente: Vente }>(`/ventes/${doc.cle}`)
        imprimerTicket(r.vente)
      } else {
        const r = await api<{ justificatif: Justificatif }>(`/justificatifs/${doc.cle}`)
        imprimerJustificatif(r.justificatif)
      }
      signaler(`${doc.numero} envoyé à l’imprimante.`)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Document introuvable.')
    } finally {
      setEnCours(null)
    }
  }

  if (!registre) return <div className="vide">Lecture du registre…</div>

  const { totaux } = registre

  return (
    <>
      <div className="barre-filtres">
        <select className="saisie" value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((t) => (
            <option key={t.cle} value={t.cle}>
              {t.libelle}
            </option>
          ))}
        </select>
        <input
          className="saisie"
          type="date"
          aria-label="Du"
          value={du}
          onChange={(e) => setDu(e.target.value)}
        />
        <input
          className="saisie"
          type="date"
          aria-label="Au"
          value={au}
          onChange={(e) => setAu(e.target.value)}
        />
        <input
          className="saisie"
          style={{ flex: 1, minWidth: 200 }}
          placeholder="Rechercher — n° de document, client…"
          value={requete}
          onChange={(e) => setRequete(e.target.value)}
        />
      </div>

      <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="bandeau-kpi">
          <div className="kpi">
            <div className="etiquette">Déposé sous contrat</div>
            <div className="valeur">{eur(totaux.contrats.centimes)}</div>
            <div className="detail">
              {totaux.contrats.nombre} contrat{totaux.contrats.nombre > 1 ? 's' : ''}
            </div>
          </div>
          <div className="kpi kpi-vedette">
            <div className="etiquette">Encaissé en caisse</div>
            <div className="valeur">{eur(totaux.tickets.centimes)}</div>
            <div className="detail">
              {totaux.tickets.nombre} ticket{totaux.tickets.nombre > 1 ? 's' : ''}
            </div>
          </div>
          <div className="kpi">
            <div className="etiquette">Remis aux déposants</div>
            <div className="valeur">{eur(totaux.justificatifs.centimes)}</div>
            <div className="detail">
              {totaux.justificatifs.nombre} justificatif
              {totaux.justificatifs.nombre > 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <section className="bloc-fiche">
          <h3 className="bloc-fiche-titre">
            {registre.documents.length} document{registre.documents.length > 1 ? 's' : ''} sur la
            période
          </h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>N°</th>
                <th>Client</th>
                <th>Détail</th>
                <th className="a-droite">Montant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {registre.documents.map((doc) => (
                <tr key={`${doc.type}-${doc.cle}`}>
                  <td style={{ whiteSpace: 'nowrap' }}>{FORMAT.format(new Date(doc.date))}</td>
                  <td>
                    <span className={`pastille-doc ${doc.type}`}>{LIBELLE_TYPE[doc.type]}</span>
                  </td>
                  <td className="ref">{doc.numero}</td>
                  <td>
                    {doc.clientId !== null ? (
                      <Link to={`/deposants/${doc.clientId}`} style={{ color: 'inherit' }}>
                        {doc.clientNom}
                      </Link>
                    ) : (
                      <span className="sourdine">—</span>
                    )}
                  </td>
                  <td className="sourdine">{doc.detail}</td>
                  <td className="a-droite chiffre">{eur(doc.montantCentimes)}</td>
                  <td className="a-droite">
                    <button
                      type="button"
                      className="btn btn-fantome"
                      disabled={enCours === doc.type + doc.cle}
                      onClick={() => void reimprimer(doc)}
                    >
                      {enCours === doc.type + doc.cle ? '…' : 'Réimprimer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {registre.documents.length === 0 && (
            <div className="vide">Aucun document sur cette période.</div>
          )}
        </section>
      </div>
    </>
  )
}
